import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Websocket } from '../../services/websocket';
import store, { AppState } from '../../stores';
import { useSelector } from 'react-redux';
import LogoWhite from '../../presentations/LogoWhite';
import mediaQuery from 'styled-media-query';
import { open as openRoomShareModal } from '../../stores/room-share-modal';
import { open as openPasswordModal } from '../../stores/room-password-modal';
import RoomShareModal from './RoomShareModal';
import { InfoType } from '../../App';
import { find } from 'lodash-es';
import { UserType } from '../../stores/authenticated';

import Favicon2 from '../../assets/favicon/favicon2.ico?url';
import RoomPasswordModal from './RoomPasswordModal';
import { appEnv } from '../../config/env';
import { api } from '../../services/api';
import {
  isAuthenticatedSessionRestored,
  recoverSessionFromHttpOnlyCookies,
} from '../../services/session-bootstrap';

declare const JitsiMeetExternalAPI: any;

function buildJitsiUserInfo(user: UserType | null): {
  displayName: string;
  email?: string;
  avatarUrl?: string;
} {
  if (user) {
    return {
      displayName: `${user.firstName} ${user.lastName}`,
      email: user.email,
      avatarUrl: user.avatar,
    };
  }
  return { displayName: 'Guest' };
}

const Room = () => {
  const init = useSelector((state: AppState) => state.appState.init);
  const connected = useSelector((state: AppState) => state.receiver.connected);
  const userId = useSelector((state: AppState) => state.authenticated.userId as number);
  const user = useSelector((state: AppState) => state.authenticated.user as UserType);
  const searchParams = new URLSearchParams(document.location.search);
  const title = searchParams.get('title') as string;
  const roomId = searchParams.get('roomId') as string;
  const inviteToken = searchParams.get('invite');
  const [lock, setLock] = useState<any>(null);
  const [participantId, setParticipantId] = useState<string>('');
  const [moderatorId, setModeratorId] = useState<string>('');
  const [sessionReady, setSessionReady] = useState(false);
  const [jitsiError, setJitsiError] = useState<string | null>(null);
  const meetRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const jitsiSetupDoneRef = useRef(false);
  const systemName = useSelector((state: AppState) => state.system.systemName);
  const roomPassword = useSelector((state: AppState) => state.roomPassword);
  const parsedDomain = new URL(appEnv.webRtcUrl);
  const jitsiMeetOrigin = parsedDomain.origin;

  const authReadyForJitsi = sessionReady && !!title && (userId == null || user != null);

  useEffect(() => {
    const faviconElm = document.querySelector('#favicon') as HTMLLinkElement;
    faviconElm.setAttribute('href', Favicon2);
  }, []);

  useEffect(() => {
    document.title = `${appEnv.name} - ${systemName}`;
  }, [systemName]);

  useEffect(() => {
    if (!init) {
      return;
    }

    let cancelled = false;

    const resolveSession = async (): Promise<void> => {
      if (isAuthenticatedSessionRestored()) {
        if (!cancelled) {
          setSessionReady(true);
        }
        return;
      }

      await recoverSessionFromHttpOnlyCookies();

      if (!cancelled) {
        setSessionReady(true);
      }
    };

    void resolveSession();

    return () => {
      cancelled = true;
    };
  }, [init]);

  useEffect(() => {
    if (!connected) {
      return;
    }

    const socket = Websocket.socket;
    if (!socket) {
      return;
    }

    const presencePayload = {
      roomId,
      userId,
      isGuest: !userId,
    };

    socket.emit('jitsiIn', presencePayload);

    const handleRoomGetInfo = ({ data }: { data: InfoType }): void => {
      const rooms = data.departments.concat(data.talkings) as any[];
      const room = find(rooms, { id: Number(roomId) });
      if (room) {
        setLock(room.isLocked);
      }
    };

    socket.on('getInfo', handleRoomGetInfo);

    return () => {
      socket.emit('jitsiOut', presencePayload);
      socket.off('getInfo', handleRoomGetInfo);
    };
  }, [connected, roomId, userId]);

  const iframePostMessage = useCallback(
    (message: unknown) => {
      const iframe = iframeRef.current ?? meetRef.current?.querySelector('iframe');
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(message, jitsiMeetOrigin);
      }
    },
    [jitsiMeetOrigin],
  );

  useEffect(() => {
    iframePostMessage({ type: 'PASSWORD', payload: { password: roomPassword.password } });
    setLock(roomPassword.isLocked);
  }, [roomPassword, iframePostMessage]);

  const handleClose = () => {
    window.close();
  };

  const handleClickLink = () => {
    const data = {
      title,
      roomId,
    };
    store.dispatch(openRoomShareModal(data));
  };

  const handleClickLock = () => {
    store.dispatch(openPasswordModal([participantId, moderatorId]));
  };

  useEffect(() => {
    if (!authReadyForJitsi) {
      return;
    }

    const container = meetRef.current;
    if (!container) {
      return;
    }

    if (!roomId || !/^\d+$/.test(roomId)) {
      setJitsiError('会議ルームへの接続に失敗しました。リンクが正しいか確認してください。');
      return;
    }

    let cancelled = false;
    let handleJitsiMessage: ((event: MessageEvent) => void) | null = null;

    const setupJitsi = async (): Promise<void> => {
      setJitsiError(null);

      let jitsiToken: string | undefined;
      let jitsiJwtEnabled = true;
      try {
        const params = new URLSearchParams({ title });
        const currentUser = store.getState().authenticated.user as UserType | null;
        if (!currentUser) {
          params.set('guestName', buildJitsiUserInfo(null).displayName);
          if (inviteToken) {
            params.set('invite', inviteToken);
          }
        }

        const { data } = await api.get<{
          enabled: boolean;
          jwt: string | null;
          expiresAt: number | null;
        }>(`/room/${roomId}/jitsi-token?${params.toString()}`);

        jitsiJwtEnabled = data?.enabled !== false;
        if (jitsiJwtEnabled) {
          if (!data?.jwt) {
            throw new Error('Missing jitsi jwt');
          }
          jitsiToken = data.jwt;
        }
      } catch {
        if (!cancelled) {
          const isGuest = !store.getState().authenticated.user;
          setJitsiError(
            isGuest && !inviteToken
              ? 'ゲスト参加には共有リンク（招待トークン付き）が必要です。'
              : '会議ルームへの接続に失敗しました。リンクが正しいか確認してください。',
          );
        }
        return;
      }

      if (cancelled || !meetRef.current || iframeRef.current) {
        return;
      }

      const iframe = document.createElement('iframe');
      iframe.src = `${jitsiMeetOrigin}/static/iframe/`;
      iframe.width = '100%';
      iframe.height = '100%';
      iframe.style.border = 'none';
      iframe.allow = 'camera; microphone; fullscreen; display-capture; autoplay';
      container.appendChild(iframe);
      iframeRef.current = iframe;

      const allowedOrigins = [jitsiMeetOrigin];

      handleJitsiMessage = (event: MessageEvent): void => {
        if (!allowedOrigins.includes(event.origin)) {
          return;
        }

        const data = event.data;
        const payload = event.data?.payload;

        switch (data.type) {
          case 'videoConferenceJoined':
            setParticipantId(payload.id);
            break;

          case 'participantRoleChanged':
            if (payload.role === 'moderator') {
              setModeratorId(payload.id);
            }
            break;

          case 'readyToClose':
            handleClose();
            break;

          default:
            break;
        }
      };

      const sendSetup = (): void => {
        if (cancelled || jitsiSetupDoneRef.current) {
          return;
        }
        jitsiSetupDoneRef.current = true;

        const currentUser = store.getState().authenticated.user as UserType | null;
        iframe.contentWindow?.postMessage(
          {
            type: 'SETUP_JITSI',
            payload: {
              roomName: title,
              ...(jitsiToken ? { jwt: jitsiToken } : {}),
              requireJwt: jitsiJwtEnabled,
              width: '100%',
              height: '100%',
              configOverwrite: {
                disableInviteFunctions: true,
                startWithVideoMuted: true,
                startWithAudioMuted: false,
                ...(jitsiJwtEnabled ? { enableUserRolesBasedOnToken: true } : {}),
                prejoinConfig: {
                  enabled: true,
                },
              },
              interfaceConfigOverwrite: {
                HIDE_INVITE_MORE_HEADER: true,
              },
              userInfo: buildJitsiUserInfo(currentUser),
            },
          },
          jitsiMeetOrigin,
        );
        window.addEventListener('message', handleJitsiMessage);
      };

      iframe.addEventListener('load', sendSetup, { once: true });
    };

    void setupJitsi();

    return () => {
      cancelled = true;
      if (handleJitsiMessage) {
        window.removeEventListener('message', handleJitsiMessage);
      }
      container.innerHTML = '';
      iframeRef.current = null;
      jitsiSetupDoneRef.current = false;
    };
  }, [authReadyForJitsi, title, jitsiMeetOrigin, roomId, inviteToken]);

  return (
    <Wrapper>
      <Header>
        <HeaderLeft>
          <LogoWhite width={104} />
        </HeaderLeft>
        <HeaderRight>
          {participantId && (
            <>
              <ToolButton type={'button'} onClick={handleClickLink} title={'共有'}>
                <i className={'soi-link'} />
              </ToolButton>
              <ToolButton type={'button'} onClick={handleClickLock} title={'ロック'} data-lock={lock}>
                <i className={'soi-lock'} />
              </ToolButton>
            </>
          )}
        </HeaderRight>
      </Header>
      <Meet ref={meetRef} />
      {jitsiError && <ErrorBanner>{jitsiError}</ErrorBanner>}
      <RoomShareModal />
      <RoomPasswordModal />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100vw;
  height: 100vh;
  position: relative;
`;

const Header = styled.header`
  padding: 0 14px;
  width: 100%;
  height: 40px;
  background: #262f40;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #fff;
`;

const HeaderLeft = styled.div`
  min-width: 104px;
  ${mediaQuery.lessThan('medium')`
    width: 23px;
    min-width: 23px;
    overflow: hidden;
    img {
      min-width: 104px;
    }
  `}
`;

const HeaderRight = styled.div``;

const ToolButton = styled.button`
  margin: 0 5px;
  padding: 10px;

  &:hover {
    opacity: 0.7;
  }

  &[data-lock='true'] {
    color: #f0b000;
  }
`;

const Meet = styled.div`
  width: 100%;
  height: calc(100% - 40px);

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const ErrorBanner = styled.div`
  position: absolute;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  max-width: calc(100% - 32px);
  padding: 12px 16px;
  background: #5c1f1f;
  color: #fff;
  border-radius: 4px;
  text-align: center;
`;

export default Room;
